import { useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, Modal, Select, Space, Table, Tabs, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { useAdminScope } from '../../context/AdminScopeContext';
import { tenantService } from '../../api/services';
import type { Tenant, TenantType } from '../../types/tenant';

const ORG_TABLE_PAGINATION = { pageSize: 20, showSizeChanger: true };
const PERSONAL_TABLE_PAGINATION = { pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] };

export function TenantListPage() {
  const navigate = useNavigate();
  const { scope } = useAdminScope();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();

  const loadTenants = async () => {
    if (!scope) return;
    setLoading(true);
    try {
      setTenants(await tenantService.listForScope(scope));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTenants();
  }, [scope]);

  const handleCreate = async () => {
    const values = await form.validateFields();
    try {
      const tenant = await tenantService.create(values as {
        name: string;
        email: string;
        code: string;
        type: TenantType;
      });
      message.success('租户创建成功');
      setModalOpen(false);
      form.resetFields();
      navigate(`/tenants/${tenant.id}`);
    } catch (err) {
      message.error(err instanceof Error ? err.message : '创建失败');
    }
  };

  const columns: ColumnsType<Tenant> = useMemo(
    () => [
      { title: '名称', dataIndex: 'name', key: 'name' },
      { title: '编码', dataIndex: 'code', key: 'code' },
      { title: '邮箱', dataIndex: 'email', key: 'email' },
      {
        title: 'Schema Profile',
        dataIndex: 'schemaProfile',
        key: 'schemaProfile',
        render: (value?: string) => value ?? '—',
      },
      {
        title: '操作',
        key: 'action',
        render: (_, record) => (
          <Button type="link" onClick={() => navigate(`/tenants/${record.id}`)}>
            管理
          </Button>
        ),
      },
    ],
    [navigate],
  );

  const isPlatformAdmin = scope?.isPlatformAdmin ?? false;
  const orgTenants = useMemo(
    () => tenants.filter((t) => t.type === 'ORGANIZATION'),
    [tenants],
  );
  const personalTenants = useMemo(
    () => tenants.filter((t) => t.type === 'PERSONAL'),
    [tenants],
  );

  const createButton = isPlatformAdmin ? (
    <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
      创建组织租户
    </Button>
  ) : null;

  const orgTable = (
    <Table
      rowKey="id"
      loading={loading}
      columns={columns}
      dataSource={orgTenants}
      pagination={ORG_TABLE_PAGINATION}
    />
  );

  return (
    <>
      {isPlatformAdmin ? (
        <Tabs
          defaultActiveKey="organization"
          tabBarExtraContent={createButton}
          items={[
            {
              key: 'organization',
              label: `组织租户 (${orgTenants.length})`,
              children: orgTable,
            },
            {
              key: 'personal',
              label: `个人租户 (${personalTenants.length})`,
              children: (
                <Table
                  rowKey="id"
                  loading={loading}
                  columns={columns}
                  dataSource={personalTenants}
                  pagination={PERSONAL_TABLE_PAGINATION}
                />
              ),
            },
          ]}
        />
      ) : (
        <>
          <Space style={{ marginBottom: 16 }}>
            <span style={{ color: '#666' }}>仅显示您作为管理员可管理的组织租户</span>
          </Space>
          {orgTable}
        </>
      )}

      <Modal
        title="创建组织租户"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleCreate}
        okText="创建"
        destroyOnHidden
      >
        <Form form={form} layout="vertical" initialValues={{ type: 'ORGANIZATION' }}>
          <Form.Item name="name" label="租户名称" rules={[{ required: true, message: '请输入租户名称' }]}>
            <Input placeholder="例如：研发平台" />
          </Form.Item>
          <Form.Item name="code" label="租户编码" rules={[{ required: true, message: '请输入租户编码' }]}>
            <Input placeholder="例如：qingfan_rd（唯一标识）" />
          </Form.Item>
          <Form.Item
            name="email"
            label="联系邮箱"
            rules={[{ required: true, type: 'email', message: '请输入有效邮箱' }]}
          >
            <Input placeholder="tenant@example.com" />
          </Form.Item>
          <Form.Item name="type" label="租户类型" hidden>
            <Select
              options={[
                { value: 'ORGANIZATION', label: '组织' },
                { value: 'PERSONAL', label: '个人' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
